import VerifiedIcon from '@mui/icons-material/Verified';
import { useCallback, useMemo } from 'react';

import { Piece, PieceAction, PieceNextAction, PieceType } from '@apiContract';
import { useSettings } from '@hooks/useSettings';
import { MailPieceInboxType } from '@rdTypes/shared';
import _ from 'lodash';

import { MailpieceAction, mailpieceActions } from '../index';
import { CombinedActions, CommonAction } from '../types';
import { isGroupEnabled } from '../utils';
import { useMailpieceActions } from './useMailpieceActions';

type BulkActionValidationResult = {
  tooltipText: string;
  disabled: boolean;
};

type BulkActionValidation = (mailpieces: Piece[]) => BulkActionValidationResult;

type BulkActionConfigItem = {
  action: PieceAction;
  bulkActionValidation?: BulkActionValidation;
};

type BulkAction = MailpieceAction & {
  id: CommonAction;
  disabled: boolean;
  tooltipText: string;
  isValidatePlaceholder?: boolean; // for disabled bulk actions in table header
};

const NOT_AVAILABLE_TOOLTIP = 'Not available for some selected items';

const VALIDATE_PLACEHOLDER_ID = -1 as CommonAction; // always disabled

const validatePlaceholder: BulkAction = {
  id: VALIDATE_PLACEHOLDER_ID,
  title: 'Validate',
  Icon: VerifiedIcon,
  disabled: true,
  tooltipText: NOT_AVAILABLE_TOOLTIP,
  isValidatePlaceholder: true,
};

const findValidationSetting = (action: PieceAction, piece: Piece): string | undefined => {
  const config = mailpieceActions[action];
  if (!config) return undefined;

  const ownSettingName =
    typeof config.validationSettingName === 'function'
      ? config.validationSettingName(piece)
      : config.validationSettingName;
  if (ownSettingName) return ownSettingName;

  if (config.group !== undefined) {
    const groupSetting = mailpieceActions[config.group]?.validationSettingName;
    return typeof groupSetting === 'string' ? groupSetting : undefined;
  }

  return undefined;
};

const getIsPieceRequiresValidation = (
  piece: Piece,
  pieceActions: PieceNextAction[],
  settings: Record<string, boolean>,
): boolean => {
  if (piece.pieceType === PieceType.Mail) return false;

  return pieceActions.some((item) => {
    if (item.isForce || item.pieceAction === undefined) return false;
    const settingName = findValidationSetting(item.pieceAction, piece);
    return !!settingName && settings[settingName];
  });
};

const isRecipientShared = (mailpieces: Piece[]) => {
  const recipientIds = _.uniq(mailpieces.map((mailpiece) => mailpiece.recipient?.user?.id));
  return recipientIds.length === 1 && !!recipientIds[0];
};

const validateSharedRecipient: BulkActionValidation = (mailpieces) => {
  const singleRecipient = isRecipientShared(mailpieces);

  return {
    tooltipText: !singleRecipient ? 'Selected items must have the same recipient' : '',
    disabled: !singleRecipient,
  };
};

const bulkActionsConfig: BulkActionConfigItem[] = [
  {
    action: PieceAction.RequestForward,
  },

  {
    action: PieceAction.RequestRecycle,
  },

  {
    action: PieceAction.RequestOpenAndScan,
  },

  {
    action: PieceAction.RequestMail,
  },

  {
    action: PieceAction.RequestPickUp,
  },

  {
    action: PieceAction.RequestShred,
  },

  {
    action: PieceAction.RequestDelivery,
  },

  {
    action: PieceAction.FinishRecycle,
  },

  {
    action: PieceAction.FinishOpenAndScan,
    bulkActionValidation: (mailpieces) => ({
      tooltipText: mailpieces.length > 1 ? 'Only one item can be opened and scanned at a time' : '',
      disabled: mailpieces.length > 1,
    }),
  },

  {
    action: PieceAction.FinishMail,
    bulkActionValidation: (mailpieces) => {
      const singleRecipient = isRecipientShared(mailpieces);
      const recipientAddresses = mailpieces.map((mailpiece) => {
        if (!mailpiece.mailTo) return null;

        const { countryCode, administrativeArea, locality, postalCode, addressLine1, addressLine2 } = mailpiece.mailTo;

        return { countryCode, administrativeArea, locality, postalCode, addressLine1, addressLine2 };
      });
      const singleAddress = recipientAddresses.every((address) => _.isEqual(address, recipientAddresses[0]));

      let tooltipText = '';

      if (!singleRecipient) {
        tooltipText = 'Selected items must have the same recipient';
      } else if (!singleAddress) {
        tooltipText = 'Selected items must have the same address';
      }

      return {
        tooltipText: tooltipText,
        disabled: !singleRecipient || !singleAddress,
      };
    },
  },

  {
    action: PieceAction.FinishPickUp,
  },

  {
    action: PieceAction.FinishShred,
  },

  {
    action: PieceAction.FinishPickUpWithSignature,
  },

  {
    action: PieceAction.FinishPickUpWithQrCode,
    bulkActionValidation: validateSharedRecipient,
  },

  {
    action: PieceAction.FinishPickupWithIdCard,
  },

  {
    action: PieceAction.FinishDelivery,
  },

  {
    action: PieceAction.FinishDeliveryWithSignature,
  },

  {
    action: PieceAction.FinishDeliveryWithImage,
  },

  {
    action: PieceAction.FinishDeliveryWithQrCode,
    bulkActionValidation: validateSharedRecipient,
  },

  {
    action: PieceAction.Archive,
  },

  {
    action: PieceAction.FinishPickupWithImage,
  },
  {
    action: PieceAction.FinishReturnToSender,
  },
  {
    action: PieceAction.FinishPickupMailbox,
  },
];

// Collapse few actions (Pick Up or Delivery) in one "Validate Button".
const combineBulkActions = (
  actions: BulkAction[],
  settings: Record<string, boolean>,
  mailpieces: Piece[],
): BulkAction[] => {
  if (mailpieces.some((piece) => piece.pieceType === PieceType.Mail)) return actions;

  const result: BulkAction[] = [];
  const groupSlot = new Map<CombinedActions, number>();

  actions.forEach((action) => {
    const { group } = action;

    if (group === undefined || !isGroupEnabled(group, settings)) {
      result.push(action);
      return;
    }

    const slot = groupSlot.get(group);

    if (slot === undefined) {
      const groupConfig = mailpieceActions[group];
      if (!groupConfig) {
        result.push(action);
        return;
      }

      groupSlot.set(group, result.length);
      result.push({
        ...groupConfig,
        id: group,
        disabled: action.disabled,
        tooltipText: action.tooltipText,
      });
    } else if (!action.disabled) {
      result[slot] = { ...result[slot], disabled: false, tooltipText: '' };
    }
  });

  return result;
};

export const useGetBulkActionsConfig = (mailpieces: Piece[], access: MailPieceInboxType) => {
  const settings: any = useSettings();
  const { bulkRegularActions, allActions } = useMailpieceActions(mailpieces, access);

  const getAction = useCallback(
    ({ action, bulkActionValidation }: BulkActionConfigItem): BulkAction | null => {
      const availableAsNormalAction = allActions.some((pieceActions) =>
        pieceActions.some((item) => item.pieceAction === action && !item.isForce),
      );

      if (!availableAsNormalAction) {
        return null;
      }

      if (!bulkRegularActions.includes(action)) {
        return null;
      }

      const actionConfig = mailpieceActions[action];
      if (!actionConfig) return null;

      let tooltipText = '';
      let disabled = false;

      const actionIsShared = allActions.every((pieceActions) =>
        pieceActions.some((item) => item.pieceAction === action && !item.isForce),
      );

      const validationRequired = !!(
        typeof actionConfig.validationSettingName === 'string' &&
        settings[actionConfig.validationSettingName] &&
        mailpieces.some((piece) => piece.pieceType !== PieceType.Mail)
      );

      const bulkActionValidationResult = bulkActionValidation?.(mailpieces);

      if (!actionIsShared) {
        tooltipText = 'Not available for some selected items';
        disabled = true;
      } else if (validationRequired) {
        tooltipText = 'Only one item can be validated at a time';
        disabled = true;
      } else if (bulkActionValidationResult?.tooltipText) {
        tooltipText = bulkActionValidationResult.tooltipText;
        disabled = bulkActionValidationResult.disabled;
      }

      return {
        id: action,
        disabled,
        tooltipText,
        ...actionConfig,
      };
    },
    [bulkRegularActions, allActions, mailpieces, settings],
  );

  return useMemo(() => {
    const actions = bulkActionsConfig.reduce((acc: BulkAction[], actionConfig) => {
      const action = getAction(actionConfig);
      if (action) {
        acc.push(action);
      }
      return acc;
    }, []);

    // Per-piece set of available (non-force) action ids
    const pieceActionSets = allActions.map((pieceActions) =>
      pieceActions.reduce((ids, item) => {
        if (!item.isForce && item.pieceAction !== undefined) {
          ids.add(item.pieceAction);
        }
        return ids;
      }, new Set<CommonAction>()),
    );

    const validationByPiece = mailpieces.map((piece, index) =>
      getIsPieceRequiresValidation(piece, allActions[index] ?? [], settings),
    );

    const hasValidationPiece = validationByPiece.some(Boolean);
    const allValidation = validationByPiece.length > 0 && validationByPiece.every(Boolean);

    const sharedValidatedActionExists =
      pieceActionSets.length > 0 &&
      [...pieceActionSets[0]].some(
        (action) =>
          pieceActionSets.every((set) => set.has(action)) &&
          mailpieces.every((piece) => {
            const settingName = findValidationSetting(action as PieceAction, piece);
            return !!settingName && settings[settingName];
          }),
      );

    const needsValidationConsolidation = hasValidationPiece && !(allValidation && sharedValidatedActionExists);

    if (!needsValidationConsolidation) {
      return combineBulkActions(actions, settings, mailpieces);
    }

    const plainActionIds = new Set<CommonAction>();
    mailpieces.forEach((_piece, index) => {
      if (!validationByPiece[index]) {
        pieceActionSets[index].forEach((action) => plainActionIds.add(action));
      }
    });

    const plainActions = actions.reduce((acc: BulkAction[], action) => {
      if (plainActionIds.has(action.id)) {
        acc.push({ ...action, disabled: true, tooltipText: NOT_AVAILABLE_TOOLTIP });
      }
      return acc;
    }, []);

    return [...plainActions, validatePlaceholder];
  }, [getAction, allActions, mailpieces, settings]);
};
